/**
 * Data loader for split JSON dictionary files
 * Implements lazy loading strategy for efficient bundle size
 */

import { WordIndex, WordDefinition } from '@/types/dictionary';
import wordsIndex from '@/data/words/index.json';
import definitionsIndex from '@/data/definitions/index.json';

// Cache for loaded data
const wordsCache = new Map<string, WordIndex[]>();
const definitionsCache = new Map<string, WordDefinition[]>();

/**
 * Get list of available word prefixes
 */
export function getAvailableWordPrefixes(): string[] {
  return wordsIndex.prefixes;
}

/**
 * Get list of available definition prefixes
 */
export function getAvailableDefinitionPrefixes(): string[] {
  return definitionsIndex.prefixes;
}

/**
 * Load words for a specific two-character prefix
 * Uses dynamic imports for code-splitting
 * @param prefix - Two-character prefix (e.g., 'co', 'cr')
 * @returns Promise resolving to array of WordIndex objects
 */
export async function loadWordsByPrefix(prefix: string): Promise<WordIndex[]> {
  const lowerPrefix = prefix.toLowerCase().substring(0, 2);

  // Check cache first
  if (wordsCache.has(lowerPrefix)) {
    return wordsCache.get(lowerPrefix)!;
  }

  // Check if prefix exists
  if (!wordsIndex.prefixes.includes(lowerPrefix)) {
    console.warn(`No words found for prefix: ${lowerPrefix}`);
    return [];
  }

  try {
    // Dynamic import for code-splitting
    const module = await import(`../../data/words/${lowerPrefix}.json`);
    const words: WordIndex[] = module.default;

    // Cache the result
    wordsCache.set(lowerPrefix, words);

    return words;
  } catch (error) {
    console.error(`Failed to load words for prefix ${lowerPrefix}:`, error);
    return [];
  }
}

/**
 * Load definitions for a specific two-character prefix
 * @param prefix - Two-character prefix
 * @returns Promise resolving to array of WordDefinition objects
 */
export async function loadDefinitionsByPrefix(prefix: string): Promise<WordDefinition[]> {
  const lowerPrefix = prefix.toLowerCase().substring(0, 2);

  // Check cache first
  if (definitionsCache.has(lowerPrefix)) {
    return definitionsCache.get(lowerPrefix)!;
  }

  // Check if prefix exists
  if (!definitionsIndex.prefixes.includes(lowerPrefix)) {
    console.warn(`No definitions found for prefix: ${lowerPrefix}`);
    return [];
  }

  try {
    const module = await import(`../../data/definitions/${lowerPrefix}.json`);
    const definitions: WordDefinition[] = module.default;

    definitionsCache.set(lowerPrefix, definitions);

    return definitions;
  } catch (error) {
    console.error(`Failed to load definitions for prefix ${lowerPrefix}:`, error);
    return [];
  }
}

/**
 * Load all words (useful for building complete indices)
 * Note: This will load all word files - use sparingly
 * @returns Promise resolving to array of all WordIndex objects
 */
export async function loadAllWords(): Promise<WordIndex[]> {
  const allWords: WordIndex[] = [];

  for (const prefix of wordsIndex.prefixes) {
    const words = await loadWordsByPrefix(prefix);
    allWords.push(...words);
  }

  return allWords;
}

/**
 * Load definition for a specific word
 * @param word - Word to find definition for
 * @returns Promise resolving to WordDefinition or null if not found
 */
export async function loadDefinitionForWord(word: string): Promise<WordDefinition | null> {
  if (word.length < 1) {
    return null;
  }

  // Extract prefix: use first 2 chars, but if second char is space/special, use first char only
  let prefix: string;
  if (word.length === 1 || word[1] === ' ') {
    prefix = word[0]!.toLowerCase();
  } else {
    prefix = word.substring(0, 2).toLowerCase();
  }

  const definitions = await loadDefinitionsByPrefix(prefix);

  return definitions.find(def => def.word.toLowerCase() === word.toLowerCase()) || null;
}

/**
 * Preload data for multiple prefixes (for optimization)
 * @param prefixes - Array of two-character prefixes to preload
 */
export async function preloadPrefixes(prefixes: string[]): Promise<void> {
  const loadPromises = prefixes.map(prefix => loadWordsByPrefix(prefix));
  await Promise.all(loadPromises);
}

/**
 * Clear all caches (useful for testing or memory management)
 */
export function clearCache(): void {
  wordsCache.clear();
  definitionsCache.clear();
}

/**
 * Get cache statistics
 */
export function getCacheStats(): {
  wordsPrefixesCached: number;
  definitionsPrefixesCached: number;
  totalWordsCached: number;
  totalDefinitionsCached: number;
} {
  let totalWordsCached = 0;
  let totalDefinitionsCached = 0;

  wordsCache.forEach(words => {
    totalWordsCached += words.length;
  });

  definitionsCache.forEach(defs => {
    totalDefinitionsCached += defs.length;
  });

  return {
    wordsPrefixesCached: wordsCache.size,
    definitionsPrefixesCached: definitionsCache.size,
    totalWordsCached,
    totalDefinitionsCached,
  };
}
